import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addPendingChange,
  removeOperationFromPending,
  updatePendingOperation,
  selectStorePendingOperationsByPatch,
  ScopedPendingOperation,
} from '../patchesSlice';
import { getOperationAmount, getOperationConflictKey, getOperationPriceRanges } from '../utils/operationIdentity';
import {
  buildPendingEntry,
  OperationEdit,
  wrapOperationNode,
  wrapOperationNodeForRemoval,
} from '../utils/pendingOperations';
import { WeightPriceRange } from '../utils/priceRanges';

/** What the user has done to an operation, if anything. */
export type OperationEditState = 'clean' | 'edited' | 'deleted';

interface UseStoreOperationEditsArgs {
  patchId: string;
  storeNumber: string;
}

/**
 * Direct, store-level editing of one patch's operations.
 *
 * Shared by the full-page operations dialog and the named price rows in the patch list, because the
 * awkward part is the same in both: an edit and a deletion on the same target must never both be
 * pending, or the submission would carry two operations competing over one thing.
 *
 * Edits are written straight into Redux, so no draft state exists to keep in sync - reopening a surface
 * shows the same work, and clearing pending changes clears it.
 */
export const useStoreOperationEdits = ({ patchId, storeNumber }: UseStoreOperationEditsArgs) => {
  const dispatch = useDispatch();
  const pendingByPatch = useSelector(selectStorePendingOperationsByPatch);

  const pendingForPatch = useMemo(() => pendingByPatch[patchId] || [], [pendingByPatch, patchId]);

  const pendingByConflictKey = useMemo(() => {
    const byKey = new Map<string, ScopedPendingOperation>();
    pendingForPatch.forEach((pending) => byKey.set(pending.entry.conflictKey, pending));
    return byKey;
  }, [pendingForPatch]);

  const pendingFor = useCallback(
    (conflictKey: string): ScopedPendingOperation | undefined => pendingByConflictKey.get(conflictKey),
    [pendingByConflictKey]
  );

  const stateFor = useCallback(
    (conflictKey: string): OperationEditState => {
      const pending = pendingByConflictKey.get(conflictKey);
      if (!pending) return 'clean';
      return pending.bucket === 'remove' ? 'deleted' : 'edited';
    },
    [pendingByConflictKey]
  );

  /** The amount to display: the pending edit's value where there is one, else the patch's own. */
  const displayedAmountFor = useCallback(
    (node: any, currentAmount?: number): number | undefined => {
      const pending = pendingByConflictKey.get(getOperationConflictKey(node));
      if (pending?.bucket === 'set') return getOperationAmount(pending.entry.minimal);
      return currentAmount;
    },
    [pendingByConflictKey]
  );

  /** The ranges to display: the pending edit's where there is one, else the patch's own. */
  const displayedPriceRangesFor = useCallback(
    (node: any, currentRanges?: any[]): any[] | undefined => {
      const pending = pendingByConflictKey.get(getOperationConflictKey(node));
      if (pending?.bucket === 'set') return getOperationPriceRanges(pending.entry.minimal) ?? currentRanges;
      return currentRanges;
    },
    [pendingByConflictKey]
  );

  const addEntry = useCallback(
    (node: any, edit: OperationEdit | undefined, bucket: 'set' | 'remove', previousValue?: number) => {
      // A removal is stored in reference form, so the entry says exactly what will be sent.
      const wrapped = bucket === 'remove' ? wrapOperationNodeForRemoval(node) : wrapOperationNode(node, edit);
      if (!wrapped) return;

      const entry = buildPendingEntry(wrapped, { kind: 'store', storeNumber, patchId }, previousValue);

      dispatch(
        addPendingChange({
          type: 'StoreOperationEdit',
          data: {
            setOperations: bucket === 'set' ? [entry] : [],
            removeOperations: bucket === 'remove' ? [entry] : [],
          },
        })
      );
    },
    [dispatch, patchId, storeNumber]
  );

  /**
   * Queues a change to this operation for this store, replacing whatever is already queued against the
   * same target - a second entry for one target would mean two operations competing over one thing.
   *
   * `previousValue` is what the patch holds today, recorded as the "before" for display.
   */
  const queueEdit = useCallback(
    (node: any, edit: OperationEdit, previousValue?: number) => {
      const pending = pendingByConflictKey.get(getOperationConflictKey(node));

      if (pending?.bucket === 'set') {
        const wrapped = wrapOperationNode(node, edit);
        if (!wrapped) return;

        dispatch(
          updatePendingOperation({
            opId: pending.entry.opId,
            minimal: wrapped.minimal,
            enriched: wrapped.enriched,
            previousValue,
          })
        );
        return;
      }

      // Pricing something queued for deletion replaces the deletion.
      if (pending?.bucket === 'remove') {
        dispatch(removeOperationFromPending({ opId: pending.entry.opId }));
      }

      addEntry(node, edit, 'set', previousValue);
    },
    [addEntry, dispatch, pendingByConflictKey]
  );

  /**
   * Queues an amount for this store, or revises the amount already queued.
   *
   * Pass `currentAmount` as undefined when the patch carries no operation for this target yet - the edit
   * then adds one.
   */
  const setAmount = useCallback(
    (node: any, amount: number, currentAmount?: number) => queueEdit(node, { amount }, currentAmount),
    [queueEdit]
  );

  /**
   * Queues a replacement set of price ranges for this store.
   *
   * No "before" is recorded: `previousValue` holds a single amount, and a set of ranges is not one. The
   * row shows the patch's own ranges until the edit is applied, which is the comparison that matters.
   */
  const setPriceRanges = useCallback(
    (node: any, weightPriceRanges: WeightPriceRange[]) => queueEdit(node, { weightPriceRanges }),
    [queueEdit]
  );

  /** Queues this operation's removal from the store's patch. */
  const removeOperation = useCallback(
    (node: any, currentAmount?: number) => {
      const pending = pendingByConflictKey.get(getOperationConflictKey(node));

      // Drop a queued edit first: both would otherwise be submitted for one target.
      if (pending?.bucket === 'set') {
        dispatch(removeOperationFromPending({ opId: pending.entry.opId }));
      }

      // No edit - a removal is a reference to the operation, identifiers only.
      addEntry(node, undefined, 'remove', currentAmount);
    },
    [addEntry, dispatch, pendingByConflictKey]
  );

  /** Discards whatever is queued for this target, restoring the patch's own value. */
  const undo = useCallback(
    (conflictKey: string) => {
      const pending = pendingByConflictKey.get(conflictKey);
      if (pending) dispatch(removeOperationFromPending({ opId: pending.entry.opId }));
    },
    [dispatch, pendingByConflictKey]
  );

  return {
    pendingCount: pendingForPatch.length,
    pendingFor,
    stateFor,
    displayedAmountFor,
    displayedPriceRangesFor,
    setAmount,
    setPriceRanges,
    removeOperation,
    undo,
  };
};
